// KNOWROK · InterventionsLayout — shell de l'univers Interventions (GMAO).
// Patron : SousTraitantLayout (header propre + bottom nav + Outlet), mais monté
// HORS AppLayout : l'univers a son propre chrome, sans la sidebar chantier.
// Accès : owner/manager avec module_maintenance (cf. GET /api/modules.universes),
// derrière le flag REACT_APP_FEATURE_UNIVERSES (PLAN_SEPARATION_UNIVERS.md).
import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { CalendarDays, FileText, Gauge, MapPin, Ticket } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useModules } from "../../hooks/useModules";
import { rememberUniverse } from "../../lib/universes";
import ThemeToggle from "../shared/ThemeToggle";
import UniverseSwitch from "../shared/UniverseSwitch";

// Simplification du 14/08 (maquette validée) : 4 onglets. Gammes, contrats,
// parc et la liste transverse des rapports restent routables par URL directe
// (rien n'est supprimé) mais sortent de la navigation.
const TABS = [
  { key: "accueil", label: "Accueil", to: "/interventions", icon: Gauge, end: true },
  { key: "lieux", label: "Lieux", to: "/interventions/lieux", icon: MapPin },
  { key: "tickets", label: "Tickets", to: "/interventions/tickets", icon: Ticket },
  // Demande owner 16/08 : centraliser les rapports à valider pour envoi —
  // l'onglet ouvre directement sur la file « En attente ».
  { key: "rapports", label: "Rapports", to: "/interventions/rapports?statut=attente", icon: FileText },
  { key: "planning", label: "Planning", to: "/interventions/planning", icon: CalendarDays },
];

function activeTab(pathname) {
  if (pathname.startsWith("/interventions/lieux")) return "lieux";
  if (pathname.startsWith("/interventions/tickets")) return "tickets";
  if (pathname.startsWith("/interventions/rapports")) return "rapports";
  if (pathname.startsWith("/interventions/planning")) return "planning";
  return "accueil";
}

export default function InterventionsLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, company } = useAuth();
  const { universes, isLoading } = useModules();
  const tab = activeTab(pathname);

  React.useEffect(() => {
    rememberUniverse("maintenance");
  }, []);

  // Confinement : l'univers Interventions est réservé aux profils annoncés par
  // le serveur (owner/manager + module actif). Pattern AppLayout:161-167.
  React.useEffect(() => {
    if (user && !isLoading && !universes.maintenance) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, isLoading, universes.maintenance, navigate]);

  const initials =
    (user?.full_name || "")
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";

  return (
    <div className="min-h-screen bg-muted font-sans pb-20 md:pb-6">
      {/* ── Header ── */}
      <header
        className="bg-card border-b border-border sticky top-0 z-20"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="px-4 py-3 flex items-center justify-between gap-3 max-w-7xl mx-auto">
          <div className="min-w-0">
            <div className="text-[10px] tracking-widest text-muted-foreground">
              ESPACE INTERVENTIONS
            </div>
            <div className="text-sm font-semibold text-foreground truncate">
              {company?.name ?? "Chargement…"}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <UniverseSwitch current="maintenance" />
            <ThemeToggle compact />
            <button
              type="button"
              aria-label="Mon compte"
              onClick={() => navigate("/settings/account")}
              className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold hover:bg-primary/90 transition-colors"
            >
              {initials}
            </button>
          </div>
        </div>

        {/* Onglets desktop */}
        <nav
          className="hidden md:flex px-4 gap-1 max-w-7xl mx-auto"
          aria-label="Sections Interventions"
        >
          {TABS.map(({ key, label, to, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => navigate(to)}
              aria-current={tab === key ? "page" : undefined}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                tab === key
                  ? "border-primary text-foreground font-medium"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon size={15} aria-hidden="true" />
              {label}
            </button>
          ))}
        </nav>
      </header>

      {/* ── Contenu ── */}
      <main className="p-4 md:p-6 max-w-7xl mx-auto" id="interventions-main">
        <Outlet />
      </main>

      {/* ── Bottom nav mobile ── */}
      <nav
        className="md:hidden fixed inset-x-0 bottom-0 bg-card border-t border-border grid grid-cols-5 z-20"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        aria-label="Navigation Interventions"
      >
        {TABS.map(({ key, label, to, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => navigate(to)}
            aria-label={label}
            aria-current={tab === key ? "page" : undefined}
            className={`py-2 flex flex-col items-center gap-0.5 transition-colors ${
              tab === key ? "text-primary" : "text-muted-foreground"
            }`}
            style={{ minHeight: 44 }}
          >
            <Icon size={20} aria-hidden="true" />
            <span className="text-[9px] font-medium">{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
